<template>
  <loading-button
    v-if="inputLoading"
    :small="small"
    :large="large"
    :full-width="fullWidth"
    :input-btn="inputBtn || iconBtn"
    :full-width-at="fullWidthAt"
  />

  <button
    v-else
    :id="inputId"
    :type="type"
    :disabled="isDisabled"
    v-bind="$attrs"
    class="tw-group mod-button-transition tw-leading-none tw-outline-none tw-items-center tw-shrink-0 tw-flex tw-justify-center tw-space-x-2 tw-font-medium"
    :class="[
      buttonDisabledBg,
      buttonUppercase,
      buttonTextSize,
      buttonOverflow,
      buttonPadding,
      buttonRadius,
      buttonBorder,
      buttonCursor,
      buttonHeight,
      buttonWidth,
      buttonType,
    ]"
    :tabindex="disabled || noTab ? '-1' : '0'"
    @click="onClick"
  >
    <!-- loader -->
    <base-progress-circle
      v-if="showProgressIndicator"
      :primary="primary"
      :percent="progress"
    />
    <base-loading v-else v-show="hasStatus" :status="status || thisStatus" />

    <!-- icon -->
    <template v-if="icon && !hasStatus">
      <base-icon v-if="baseIcon" :icon="icon" class="tw-size-6" />
      <font-awesome-icon
        v-else
        :class="[Classes.BASE_BUTTON_ICON, iconColor]"
        :icon="icon"
        :size="iconSize"
        fixed-width
      />
    </template>

    <!-- slot data -->
    <span
      v-if="$slots.default && !hasStatus"
      class="tw-transform tw-transition-transform"
      :class="[
        textAnimation,
        {
          'tw-w-full': slotFullWidth,
          'tw-truncate': truncate,
        },
      ]"
    >
      <slot />
    </span>

    <!-- screen reader text -->
    <span v-if="srOnly" class="sr-only">{{ srOnly }}</span>
  </button>
</template>

<script>
import BaseProgressCircle from '@components/Misc/BaseProgressCircle.vue'
import LoadingButton from '@components/Loading/LoadingButton.vue'
import { isObjEmpty, genHtmlId } from '@helpers/utils.js'
import Classes from '@shared/js/config/css.js'
import { useStatus } from '@composables'
import { computed, toRefs } from 'vue'
export default {
  components: {
    BaseProgressCircle,
    LoadingButton,
  },
  props: {
    basicBtn: {
      type: Boolean,
      default: false,
    },
    inputBtn: {
      type: Boolean,
      default: false,
    },
    textLink: {
      type: Boolean,
      default: false,
    },
    textLinkHover: {
      type: Boolean,
      default: false,
    },
    primary: {
      type: Boolean,
      default: false,
    },
    secondary: {
      type: Boolean,
      default: false,
    },
    success: {
      type: Boolean,
      default: false,
    },
    info: {
      type: Boolean,
      default: false,
    },
    warning: {
      type: Boolean,
      default: false,
    },
    danger: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    noBg: {
      type: Boolean,
      default: false,
    },
    xLarge: {
      type: Boolean,
      default: false,
    },
    large: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    xsmall: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    slotFullWidth: {
      type: Boolean,
      default: false,
    },
    autoWidth: {
      type: Boolean,
      default: false,
    },
    autoHeight: {
      type: Boolean,
      default: false,
    },
    fullWidthAt: {
      type: String,
      default: '',
      validator: (value) => ['xl', 'lg', 'md', 'sm', 'xs', ''].includes(value),
    },
    uppercase: {
      type: Boolean,
      default: false,
    },
    radius: {
      type: String,
      default: 'default',
      validator: (value) =>
        ['full', 'bottom', 'left', 'right', 'none', 'default'].includes(value),
    },
    icon: {
      type: [String, Array],
      default: null,
    },
    iconSize: {
      type: String,
      default: null,
    },
    baseIcon: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    status: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'button',
    },
    id: {
      type: String,
      default: null,
    },
    srOnly: {
      type: String,
      default: '',
    },
    noTab: {
      type: Boolean,
      default: false,
    },
    inputLoading: {
      type: Boolean,
      default: false,
    },
    progress: {
      type: Number,
      default: 0,
    },
    iconColor: {
      type: String,
      default: 'tw-fill-current',
    },
    truncate: {
      type: Boolean,
      default: false,
    },
    noPadding: {
      type: Boolean,
      default: false,
    },
    smallPadding: {
      type: Boolean,
      default: false,
    },
    overflow: {
      type: Boolean,
      default: false,
    },
    hollow: {
      type: Boolean,
      default: false,
    },
    table: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { slots }) {
    const {
      textLinkHover,
      smallPadding,
      fullWidthAt,
      autoHeight,
      uppercase,
      fullWidth,
      autoWidth,
      secondary,
      noPadding,
      overflow,
      disabled,
      basicBtn,
      inputBtn,
      progress,
      textLink,
      readOnly,
      warning,
      success,
      primary,
      errors,
      active,
      radius,
      status,
      hollow,
      danger,
      xLarge,
      xsmall,
      table,
      large,
      small,
      info,
      icon,
      id,
    } = toRefs(props)

    const inputId = id.value ? id.value : genHtmlId()
    const {
      status: thisStatus,
      statusType,
      watchStatusAnimation,
      updateStatus,
    } = useStatus()

    const iconBtn = computed(() => icon.value && !slots.default)

    const { isLoading, isSuccess, isError } = watchStatusAnimation(status)

    const isTextLinkItem = computed(() => textLink.value || textLinkHover.value)

    const isDisabled = computed(() => !!status.value || disabled.value)

    const hasStatus = computed(
      () =>
        isLoading.value || isSuccess.value || isError.value || thisStatus.value
    )

    const showProgressIndicator = computed(
      () => isLoading.value && progress.value && progress.value < 1
    )

    const buttonWidth = computed(() => {
      if (table.value) {
        if (autoWidth.value || textLink.value) return ''
        else if (iconBtn.value) return 'mod-input--width-small'
        return 'tw-min-w-18'
      }
      if (fullWidth.value) {
        return 'tw-w-full'
      } else if (
        basicBtn.value ||
        autoWidth.value ||
        (isTextLinkItem.value && !iconBtn.value)
      ) {
        return ''
      } else if (iconBtn.value) {
        if (small.value) {
          return 'mod-input--width-small'
        } else if (xsmall.value) {
          return 'mod-input--width-xsmall'
        }
        return isTextLinkItem.value
          ? 'mod-input--width-small'
          : large.value
            ? 'mod-input--width-large'
            : 'mod-input--width'
      } else if (inputBtn.value) {
        if (large.value) {
          return 'mod-input--width-large'
        } else if (small.value) {
          return 'mod-input--width-small'
        }
        return 'mod-input--width'
      } else {
        if (fullWidthAt.value === 'xl') {
          return `tw-w-full xl:tw-w-36`
        } else if (fullWidthAt.value === 'lg') {
          return `tw-w-full lg:tw-w-36`
        } else if (fullWidthAt.value === 'md') {
          return `tw-w-full md:tw-w-36`
        } else if (fullWidthAt.value === 'sm') {
          return `tw-w-full sm:tw-w-36`
        } else if (fullWidthAt.value === 'xs') {
          return `tw-w-full xs:tw-w-36`
        } else {
          return 'tw-w-36'
        }
      }
    })

    const buttonHeight = computed(() => {
      if (table.value) {
        return 'mod-input--height-small'
      }
      if (xLarge.value && !basicBtn.value) {
        return 'tw-h-16'
      }
      if (large.value && !basicBtn.value) {
        return 'mod-input--height-large'
      } else if (small.value) {
        return 'mod-input--height-small'
      } else if (xsmall.value) {
        return 'mod-input--height-xsmall'
      } else if (
        autoHeight.value ||
        basicBtn.value ||
        (isTextLinkItem.value && !iconBtn.value)
      ) {
        return ''
      } else {
        return 'mod-input--height'
      }
    })

    const buttonTextSize = computed(() => {
      if (large.value) {
        return 'tw-text-md'
      } else if (small.value) {
        return 'tw-text-sm'
      }
      return 'tw-text-sm'
    })

    const buttonCursor = computed(() => {
      if (hasStatus.value || readOnly.value) {
        return 'tw-cursor-default'
      } else if (disabled.value || isDisabled.value) {
        return 'tw-cursor-not-allowed'
      } else {
        return 'tw-cursor-pointer'
      }
    })

    const buttonDisabledBg = computed(() =>
      disabled.value ? 'tw-opacity-40' : ''
    )

    const buttonType = computed(() => {
      if (textLink.value && !primary.value && !success.value && !danger.value)
        return ''
      if (textLink.value)
        return 'game-link-color hover:game-link-color-hover focus:game-link-color-hover'
      if (textLinkHover.value)
        return 'hover:game-link-color focus:game-link-color'

      if (active.value || hollow.value) {
        return _checkStatus(
          'game-active-button-color game-active-text hover:game-active-button-color-hover focus:game-active-button-color-hover hover:game-active-text-hover focus:game-active-text-hover'
        )
      } else if (primary.value) {
        return _checkStatus([
          'game-primary-button-color game-primary-text',
          {
            'hover:game-primary-button-color-hover focus:game-primary-button-color-hover hover:game-primary-text-hover focus:game-primary-text-hover':
              !readOnly.value,
          },
        ])
      } else if (secondary.value) {
        return _checkStatus(
          'game-secondary-button-color game-secondary-text hover:game-secondary-button-color-hover focus:game-secondary-button-color-hover hover:game-secondary-text-hover focus:game-secondary-text-hover'
        )
      } else if (info.value) {
        return _checkStatus(
          'tw-bg-info tw-text-info-text hover:tw-bg-info-hover focus:tw-bg-info-hover'
        )
      } else if (success.value) {
        return _checkStatus(
          'tw-bg-success tw-text-success-text hover:tw-bg-success-hover focus:tw-bg-success-hover'
        )
      } else if (warning.value) {
        return _checkStatus(
          'tw-bg-warning tw-text-warning-text hover:tw-bg-warning-hover focus:tw-bg-warning-hover'
        )
      } else if (danger.value) {
        return _checkStatus(
          'tw-bg-danger tw-text-danger-text hover:tw-bg-danger-hover focus:tw-bg-danger-hover'
        )
      }

      return ''
    })

    const buttonBorder = computed(() => {
      if (isTextLinkItem.value) return ''

      const border = 'tw-border-2'

      if (active.value || hollow.value)
        return `${border} game-active-button-border-color hover:game-active-button-border-color-hover focus:game-active-button-border-color-hover`
      if (primary.value)
        return [
          border,
          'game-primary-button-border-color',
          {
            'hover:game-primary-button-border-color-hover focus:game-primary-button-border-color-hover':
              !readOnly.value,
          },
        ]
      if (secondary.value)
        return `${border} game-secondary-button-border-color hover:game-secondary-button-border-color-hover focus:game-secondary-button-border-color-hover`
      if (info.value)
        return `${border} tw-border-info hover:tw-border-info-hover focus:tw-border-info-hover`
      if (success.value)
        return `${border} tw-border-success hover:tw-border-success-hover focus:tw-border-success-hover`
      if (warning.value)
        return `${border} tw-border-warning hover:tw-border-warning-hover focus:tw-border-warning-hover`
      if (danger.value)
        return `${border} tw-border-danger hover:tw-border-danger-hover focus:tw-border-danger-hover`

      return `${border} tw-border-transparent`
    })

    const buttonRadius = computed(() => {
      if (radius.value === 'none' || isTextLinkItem.value) return ''

      switch (radius.value) {
        case 'bottom':
          return 'game-button-radius tw-rounded-t-none'
        case 'full':
          return 'tw-rounded-full'
        case 'left':
          return 'game-button-radius tw-rounded-r-none'
        case 'right':
          return 'game-button-radius tw-rounded-l-none'

        default:
          return 'game-button-radius'
      }
    })

    const buttonUppercase = computed(() =>
      uppercase.value ? 'tw-uppercase' : ''
    )

    const buttonPadding = computed(() =>
      noPadding.value ? '' : smallPadding.value ? 'tw-px-1' : 'tw-px-3'
    )

    const buttonOverflow = computed(() => ({
      'tw-overflow-hidden': !overflow.value,
    }))

    const textAnimation = computed(() =>
      icon.value
        ? ''
        : hasStatus.value
          ? 'tw-translate-x-px'
          : 'tw-translate-x-0'
    )

    function _checkStatus(currentBg) {
      if (hasStatus.value) {
        if (isLoading.value) {
          return currentBg
        } else if (isSuccess.value) {
          return 'tw-bg-success tw-text-success-text tw-border-success active:tw-border-success hover:tw-border-success focus:tw-border-success'
        } else if (isError.value) {
          return 'tw-bg-danger tw-text-danger-text tw-border-danger active:tw-border-danger hover:tw-border-danger focus:tw-border-danger'
        }
      }
      return currentBg
    }

    function onClick() {
      if (errors.value && !isObjEmpty(errors.value)) {
        if (Object.values(errors.value).some((errArray) => errArray.length)) {
          updateStatus(statusType.ERROR)
        }
      }
    }

    return {
      showProgressIndicator,
      buttonDisabledBg,
      buttonUppercase,
      buttonTextSize,
      isTextLinkItem,
      buttonOverflow,
      textAnimation,
      buttonPadding,
      buttonCursor,
      buttonBorder,
      buttonRadius,
      buttonHeight,
      buttonWidth,
      isDisabled,
      buttonType,
      thisStatus,
      hasStatus,
      iconBtn,
      Classes,
      inputId,
      onClick,
    }
  },
}
</script>
